/**
 * Design tokens — the axis-identity palette (Deliverable C, §5.3).
 *
 * The three axes (Warrant / Empirical / Modal) must always read as their own
 * colour — they never blur — and the verdict states (explorable / refuted /
 * outside) and the three integrity lanes (verified / unverified / conflict) each
 * get their own token. Semantics live in the *token name*; a theme is just a
 * different set of values for the same names, so theming is a pure swap that
 * cannot change meaning. Tokens are surfaced to the DOM as CSS custom properties
 * (`--pv-*`) that the renderer's class rules consume via `var(...)`.
 */
/** The CSS custom-property *name* a token maps to (foreground). */
export function tokenVar(token) {
    return `--pv-${token}`;
}
/** The CSS custom-property name for a token's surface tint. */
export function tokenSurfaceVar(token) {
    return `--pv-${token}-surface`;
}
/**
 * The default ("classic") theme. Each axis is a clearly distinct hue; the verdict
 * states use an intuitive but accessible set (explorable = calm blue, refuted =
 * red, outside = muted grey — outside is "not even stated", deliberately quiet so
 * it never looks like a refutation); the lanes use green / neutral / amber.
 * Colours are chosen for >= 4.5:1 contrast of `fg` on a light background.
 */
export const DEFAULT_THEME = Object.freeze({
    'axis-warrant': { fg: '#1f5fbf', surface: '#e8f0fc' },
    'axis-empirical': { fg: '#0f7a52', surface: '#e3f4ec' },
    'axis-modal': { fg: '#7a3fb0', surface: '#f0e8f8' },
    'state-explorable': { fg: '#1f6f8b', surface: '#e4f1f5' },
    'state-refuted': { fg: '#b3261e', surface: '#fbe7e6' },
    'state-outside': { fg: '#5f6368', surface: '#eef0f1' },
    'lane-verified': { fg: '#1e7a3c', surface: '#e6f4ea' },
    'lane-unverified': { fg: '#5f6368', surface: '#eef0f1' },
    // darkened in Phase 3 so the foreground meets WCAG AA (>=4.5:1) on its OWN
    // tinted surface, not only on white (the previous #9a6700 was 4.42:1 on surface).
    'lane-conflict': { fg: '#8a5c00', surface: '#fdf3e0' },
    neutral: { fg: '#202124', surface: 'transparent' },
});
/**
 * The deep-neutral page background the dark theme is designed against. The
 * `neutral` token's surface is `transparent`, so the base text (`neutral.fg`)
 * sits directly on this colour; the harness chrome paints it as the page
 * background. Exported so a host and the contrast test reference one source of
 * truth (the dark theme's AA bar is checked against this for the transparent
 * `neutral` surface, and against each token's own surface otherwise).
 */
export const DARK_PAGE_BG = '#14161b';
/**
 * A dark theme — the harness's default (the one visual commitment, function-first).
 * Same token *names* as every theme (theming is a pure value swap that cannot
 * change meaning), with values chosen for a deep-neutral dark page: a light base
 * text, and every semantic token kept clearly distinguishable as its own hue —
 * the three axes (warrant blue / empirical green / modal purple), the verdict
 * states (explorable calm cyan / refuted red / outside a quiet grey so it never
 * reads as a refutation), and the three lanes (verified green / unverified grey /
 * conflict amber). Each `fg` meets WCAG-AA (>= 4.5:1) on its OWN dark `surface`
 * (and `neutral.fg` on {@link DARK_PAGE_BG}) — verified in `tokens.test.ts`.
 */
export const DARK_THEME = Object.freeze({
    'axis-warrant': { fg: '#7fb4ff', surface: '#1a2230' },
    'axis-empirical': { fg: '#5fd6a4', surface: '#13261e' },
    'axis-modal': { fg: '#cba6f0', surface: '#241a30' },
    'state-explorable': { fg: '#6fd2e6', surface: '#102329' },
    'state-refuted': { fg: '#ff8a80', surface: '#2e1614' },
    // outside is "not even stated": a quiet, desaturated grey, never a red-like alarm.
    'state-outside': { fg: '#a8b0bb', surface: '#1c1f25' },
    'lane-verified': { fg: '#69d68a', surface: '#13261b' },
    'lane-unverified': { fg: '#a8b0bb', surface: '#1c1f25' },
    // precision-loss is rendered with the `state-outside` token (`.pv-loss`), so the
    // subdued grey above doubles as the dark theme's subdued precision-loss treatment.
    'lane-conflict': { fg: '#f2c14e', surface: '#2a2210' },
    neutral: { fg: '#e6e8ec', surface: 'transparent' },
});
/** A high-contrast theme — same names, different values (theming = pure swap). */
export const HIGH_CONTRAST_THEME = Object.freeze({
    'axis-warrant': { fg: '#0000c0', surface: '#ffffff' },
    'axis-empirical': { fg: '#005000', surface: '#ffffff' },
    'axis-modal': { fg: '#5a008a', surface: '#ffffff' },
    'state-explorable': { fg: '#004a6e', surface: '#ffffff' },
    'state-refuted': { fg: '#a00000', surface: '#ffffff' },
    'state-outside': { fg: '#303030', surface: '#ffffff' },
    'lane-verified': { fg: '#005000', surface: '#ffffff' },
    'lane-unverified': { fg: '#303030', surface: '#ffffff' },
    'lane-conflict': { fg: '#6a3a00', surface: '#ffffff' },
    neutral: { fg: '#000000', surface: 'transparent' },
});
const THEME_KEYS = [
    'axis-warrant',
    'axis-empirical',
    'axis-modal',
    'state-explorable',
    'state-refuted',
    'state-outside',
    'lane-verified',
    'lane-unverified',
    'lane-conflict',
    'neutral',
];
/** Emit a theme as a CSS custom-property declaration block, scoped to a selector. */
export function themeToCssVars(theme, selector = '.pv-root') {
    const lines = [];
    for (const key of THEME_KEYS) {
        const color = theme[key];
        lines.push(`  ${tokenVar(key)}: ${color.fg};`);
        lines.push(`  ${tokenSurfaceVar(key)}: ${color.surface};`);
    }
    return `${selector} {\n${lines.join('\n')}\n}`;
}
/**
 * The renderer's structural stylesheet. Uses **logical properties only**
 * (`margin-inline`, `padding-inline`, `border-inline-start`, `text-align: start`)
 * so layout is direction-agnostic by construction and RTL falls out in Phase 2
 * with no rule changes. Token classes consume the custom properties via `var()`,
 * so a theme swap re-paints without touching markup or these rules.
 */
export function structuralCss() {
    return [
        '.pv-root { color: var(--pv-neutral); text-align: start; font: inherit; }',
        '.pv-node { margin-block: 0.15rem; }',
        '.pv-section { margin-block-end: 1rem; }',
        '.pv-claim { padding-inline-start: 0.75rem; border-inline-start: 3px solid var(--pv-neutral); margin-block: 0.5rem; }',
        '.pv-axes { display: flex; gap: 0.75rem; flex-wrap: wrap; }',
        // one rule family per token: foreground + surface, both from custom props.
        ...THEME_KEYS.filter((k) => k !== 'neutral').map((k) => `.tok-${k} { color: var(${tokenVar(k)}); background-color: var(${tokenSurfaceVar(k)}); border-inline-start-color: var(${tokenVar(k)}); }`),
        '.pv-loss { color: var(--pv-state-outside); font-style: italic; padding-inline-start: 0.5rem; }',
        '.pv-loss[data-severity="high"] { font-weight: 600; }',
        // Phase 2: interactive shell + lane layout. Logical properties only so the
        // whole instrument mirrors correctly under dir="rtl" with no rule changes.
        '.pv-app { display: flex; flex-direction: column; gap: 1rem; text-align: start; }',
        '.pv-app-controls { display: flex; flex-direction: column; gap: 0.5rem; }',
        '.pv-ctl-row, .pv-ctl-registers, .pv-ctl-edges, .pv-ctl-promote, .pv-ctl-history { display: flex; gap: 0.5rem; flex-wrap: wrap; }',
        '.pv-btn { padding-block: 0.25rem; padding-inline: 0.6rem; border: 1px solid var(--pv-neutral); border-radius: 4px; background: transparent; color: inherit; }',
        '.pv-btn[aria-pressed="true"] { border-color: var(--pv-axis-warrant); color: var(--pv-axis-warrant); font-weight: 600; }',
        '.pv-btn:disabled { opacity: 0.5; }',
        '.pv-promote-rejected { color: var(--pv-state-refuted); padding-inline-start: 0.5rem; }',
        '.pv-promote-accepted { color: var(--pv-lane-verified); padding-inline-start: 0.5rem; }',
        '.pv-promote-reason { margin-inline-start: 0.4rem; }',
        // the three integrity lanes, side by side, each on its own track
        '.pv-node[data-token] > .pv-text { margin-inline-end: 0.4rem; }',
        // --- accessibility (Phase 3): visible focus + dismiss affordance ---
        // A clearly visible focus ring on every interactive control (WCAG 2.4.7),
        // using outline (direction-agnostic) so it is unaffected by RTL.
        '.pv-btn:focus-visible, .pv-loss-dismiss:focus-visible { outline: 3px solid var(--pv-axis-warrant); outline-offset: 2px; }',
        // Fallback for engines without :focus-visible — never remove focus styling.
        '.pv-btn:focus, .pv-loss-dismiss:focus { outline: 3px solid var(--pv-axis-warrant); outline-offset: 2px; }',
        '.pv-loss { display: inline-flex; align-items: baseline; gap: 0.35rem; }',
        '.pv-loss-dismiss { border: 0; background: transparent; color: inherit; cursor: pointer; font-size: 1em; line-height: 1; padding-inline: 0.25rem; }',
        '.pv-loss-summary { list-style: none; padding-inline-start: 0; margin-block: 0.5rem; }',
        // honour reduced-motion (no animations are used, but declare intent).
        '@media (prefers-reduced-motion: reduce) { .pv-node, .pv-btn { transition: none; } }',
    ].join('\n');
}
/** The full stylesheet (theme vars + structural rules) for a given theme. */
export function buildStylesheet(theme = DEFAULT_THEME) {
    return `${themeToCssVars(theme)}\n${structuralCss()}`;
}
//# sourceMappingURL=tokens.js.map